import {
  ArtistIntakeSchema,
  LocalArtistIntakeSchema,
  DarkRoomReviewSchema,
  type ArtistIntake,
  type ConsentAcceptance,
  type DarkRoomReview
} from "./schemas";
import { resolveGenreBenchmark } from "./product";
import { requireAnalysisConsent } from "./consent";
import { assessmentMissingEvidence, buildAssessmentEnvelope, createEvidencePersonaReviews } from "./assessment";

export interface ShadowArtistRecord {
  id: string;
  intake: ArtistIntake;
  consent: ConsentAcceptance;
  createdAt: string;
}

export function createMockIntake(intake: ArtistIntake, consent: ConsentAcceptance, lane: "local" | "server" = "server"): ShadowArtistRecord {
  const parsedIntake = (lane === "local" ? LocalArtistIntakeSchema : ArtistIntakeSchema).parse(intake);
  const parsedConsent = requireAnalysisConsent(consent);

  return {
    id: `art_${crypto.randomUUID()}`,
    intake: parsedIntake,
    consent: parsedConsent,
    createdAt: new Date().toISOString()
  };
}

const SPECIFICITY_KEYWORDS = ["sync", "distribution", "splits", "licensing", "publishing", "placement", "sync licensing"];

function evidenceBand(density: number): "thin" | "medium" | "dense" {
  if (density < 0.35) return "thin";
  if (density < 0.7) return "medium";
  return "dense";
}

function goalsSpecificityBonus(goals: string[]): number {
  const joined = goals.join(" ").toLowerCase();
  return SPECIFICITY_KEYWORDS.filter((kw) => joined.includes(kw)).length * 4;
}

function adaptCritiqueTone(critique: string, mode: string): string {
  if (mode === "constructive") {
    return critique + " Here's how to move forward: treat this feedback as a specific gap to close, not a verdict on potential.";
  }
  if (mode === "mentor") {
    return "You're in good company — every artist at this stage faces this. " + critique + " Take the next step with intention, not urgency.";
  }
  return critique;
}

export function runDarkRoomLiteMock(artist: ShadowArtistRecord, songText: string): DarkRoomReview {
  // Local rules are cheap. Fresh results avoid retaining artist data or sharing
  // personalized drafts between requests; use the complete evidence each time.
  const result = runDarkRoomLiteMockInner(artist, songText);
  const mode = artist.intake.critiqueMode ?? "direct";
  if (mode !== "direct") {
    const adapted = {
      ...result,
      personaReviews: result.personaReviews.map((pr) => ({
        ...pr,
        critique: pr.critique ? adaptCritiqueTone(pr.critique, mode) : pr.critique
      }))
    };
    return adapted;
  }
  return result;
}

function runDarkRoomLiteMockInner(artist: ShadowArtistRecord, songText: string): DarkRoomReview {
  const evidenceDensity = Math.min(1, Math.max(0.15, songText.trim().length / 480));
  const band = evidenceBand(evidenceDensity);
  const goals = artist.intake.goals.length;
  const rightsReady = artist.consent.rightsAttestation && artist.consent.aiDisclosureAcknowledged;

  // Genre benchmark adjustment: anchor readiness closer to genre avg
  const benchmark = resolveGenreBenchmark(artist.intake.genre ?? undefined);
  const genreReadinessAdj = benchmark ? Math.round((benchmark.avgReadiness - 65) * 0.25) : 0;

  // Goals specificity bonus: concrete goals like "sync licensing" score higher
  const specificityBonus = goalsSpecificityBonus(artist.intake.goals);

  // Evidence density bands: dense evidence unlocks higher music/scribe scores
  const densityMusicBonus = band === "dense" ? 10 : band === "medium" ? 0 : -8;
  const densityIdentityBonus = band === "thin" ? -5 : 0;

  const cadenceBonus = artist.intake.previousRelease ? 6 : 0;
  const identityScore = clampScore(62 + goals * 5 + (artist.intake.genre ? 8 : 0) + densityIdentityBonus);
  const musicScore = clampScore(55 + evidenceDensity * 24 + densityMusicBonus);
  const readinessScore = clampScore((rightsReady ? 74 : 42) + genreReadinessAdj + specificityBonus);
  const marketScore = clampScore(50 + goals * 4 + (artist.intake.links.length > 0 ? 7 : 0) + specificityBonus + cadenceBonus);
  const studioFit = clampScore((identityScore + readinessScore + marketScore) / 3);
  const overall = clampScore((musicScore + identityScore + readinessScore + marketScore + studioFit) / 5);

  const personaReviews = createEvidencePersonaReviews(artist, songText, { music: musicScore, identity: identityScore, readiness: readinessScore, market: marketScore, studioFit, overall });

  return DarkRoomReviewSchema.parse({
    id: `rev_${crypto.randomUUID()}`,
    artistId: artist.id,
    artistName: artist.intake.artistName,
    reviewType: "dark_room_lite",
    assessment: buildAssessmentEnvelope(artist, songText),
    personaReviews,
    compositeScores: {
      music: musicScore,
      identity: identityScore,
      catalog: evidenceDensity > 0.5 ? 61 : 38,
      market: marketScore,
      readiness: readinessScore,
      studioFit,
      overall
    },
    confidence: "low",
    recommendation: {
      suggestedDepth: overall >= 82 ? "Echo" : overall >= 68 ? "Trace" : "Signal",
      artistStatus: overall >= 76 && rightsReady ? "dark_room_recommended" : "shadow_user",
      escalationFlags: rightsReady ? [] : ["rights_or_disclosure_incomplete"],
      topActions: [
        "Complete Release Room rights and disclosure checklist.",
        "Add one full track, lyric sheet, or performance reference.",
        "Draft the Seamline public artist page before any distribution move."
      ],
      missingInfo: buildMissingInfo(artist, songText)
    },
    seamlineDraft: {
      artistId: artist.id,
      artistName: artist.intake.artistName,
      headline: `${artist.intake.artistName} is building a ${artist.intake.genre ?? "genre-fluid"} signal with SHADOW.`,
      bioShort: `${artist.intake.artistName} is in SHADOW Signal review: identity, readiness, rights, and next actions are being clarified before release pressure takes over.`,
      bioLong: `${artist.intake.artistName} entered SHADOW for a first-pass Dark Room Lite read. This draft is not public until the artist approves it and all rights/disclosure checks are complete.`,
      links: artist.intake.links,
      featuredTrack: null,
      theme: "dark_minimal",
      cta: "Listen / Contact",
      shadowBadge: true,
      proofItems: [],
      privacy: artist.intake.privacyLevel === "public_only" ? "public" : "private"
    },
    synthaHandoff: {
      artistId: artist.id,
      status: rightsReady ? "draft" : "blocked",
      validationErrors: rightsReady ? [] : ["rights_attestation_required", "ai_disclosure_required"],
      includeAudio: false
    }
  });
}

function buildMissingInfo(artist: ShadowArtistRecord, songText: string): string[] {
  return assessmentMissingEvidence(artist, songText);
}

function clampScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export interface EvidenceQualityReport {
  tier: "thin" | "basic" | "solid" | "rich";
  score: number;
  metrics: {
    textLength: number;
    linkCount: number;
    hasGenre: boolean;
    hasPreviousRelease: boolean;
    hasSpecificityKeywords: boolean;
  };
  gaps: string[];
  recommendation: string;
}

const EVIDENCE_SPECIFICITY_KEYWORDS = [
  "sync", "distribution", "splits", "licensing", "publishing",
  "placement", "mastering", "mixing", "catalog", "EP", "album",
  "single", "feature", "collab", "rollout", "campaign"
];

export function scoreEvidenceQuality(intake: ArtistIntake, songText: string): EvidenceQualityReport {
  const textLength = songText.trim().length;
  const linkCount = intake.links.length;
  const hasGenre = !!intake.genre?.trim();
  const hasPreviousRelease = !!(intake as { previousRelease?: unknown }).previousRelease;
  const lowerText = (songText + " " + (intake.genre ?? "")).toLowerCase();
  const hasSpecificityKeywords = EVIDENCE_SPECIFICITY_KEYWORDS.some((kw) => lowerText.includes(kw.toLowerCase()));

  let score = 0;
  if (textLength >= 120) score += 30;
  else if (textLength >= 60) score += 15;
  if (linkCount >= 2) score += 25;
  else if (linkCount >= 1) score += 12;
  if (hasGenre) score += 20;
  if (hasPreviousRelease) score += 15;
  if (hasSpecificityKeywords) score += 10;

  const tier: EvidenceQualityReport["tier"] =
    score >= 80 ? "rich" : score >= 55 ? "solid" : score >= 30 ? "basic" : "thin";

  const gaps: string[] = [];
  if (textLength < 120) gaps.push("Add more lyric or creative text evidence (min 120 characters).");
  if (linkCount < 2) gaps.push("Add at least 2 public reference links.");
  if (!hasGenre) gaps.push("Specify your genre or artistic lane.");
  if (!hasPreviousRelease) gaps.push("Log a previous release for cadence context.");
  if (!hasSpecificityKeywords) gaps.push("Include specifics: distribution, publishing, licensing, or rollout details.");

  const recommendation =
    tier === "rich" ? "Several preparation inputs are supplied; musical quality and rights still require independent review."
    : tier === "solid" ? "Several preparation inputs are present. Record missing context before deeper review."
    : tier === "basic" ? "Enough to run a basic Signal. Expand text and links for a deeper read."
    : "Evidence is too thin for a reliable evaluation. Add lyrics, links, and genre before rerunning.";

  return { tier, score, metrics: { textLength, linkCount, hasGenre, hasPreviousRelease, hasSpecificityKeywords }, gaps, recommendation };
}
